// Commission agents slice.
//
// One normalized list for agents, one for the selected agent's banks, and a
// per-operation write state for each mutation, all from the shared factories.
// `list.byGuid` doubles as the agent-name cache every other commissions screen
// reads, so detail and bulk-resolve fetches merge into it.

import * as types from '../../lib/actionTypes.es6';
import {
  createListReducer,
  createWriteReducer,
  initialListState,
  initialWriteState,
  readListResponse
} from '../../components/CommissionsShared/listReducerFactory.es6';

var agentsList = createListReducer(
  {
    request: types.GET_COMMISSION_AGENTS_REQUEST,
    success: types.GET_COMMISSION_AGENTS_SUCCESS,
    failure: types.GET_COMMISSION_AGENTS_FAILURE,
    detailSuccess: types.GET_COMMISSION_AGENT_SUCCESS,
    mergeMany: types.RESOLVE_COMMISSION_AGENTS_SUCCESS
  },
  // Keep the current page visible while the next one loads, so changing a
  // filter does not blank the table mid-read.
  { keepRowsWhileLoading: true }
);

var banksList = createListReducer({
  request: types.GET_AGENT_BANKS_BY_AGENT_REQUEST,
  success: types.GET_AGENT_BANKS_BY_AGENT_SUCCESS,
  failure: types.GET_AGENT_BANKS_BY_AGENT_FAILURE
});

// Per-operation write states. Reset entries are optional.
var WRITES = {
  create: {
    request: types.CREATE_COMMISSION_AGENT_REQUEST,
    success: types.CREATE_COMMISSION_AGENT_SUCCESS,
    failure: types.CREATE_COMMISSION_AGENT_FAILURE,
    reset: types.RESET_CREATE_COMMISSION_AGENT_SUCCESS
  },
  // The create-user-then-agent chain. Its own write state, because it can end in
  // a third outcome the plain triad cannot express: user created, agent not.
  createWithUser: {
    request: types.CREATE_AGENT_WITH_USER_REQUEST,
    success: types.CREATE_AGENT_WITH_USER_SUCCESS,
    failure: types.CREATE_AGENT_WITH_USER_FAILURE,
    reset: types.RESET_CREATE_COMMISSION_AGENT_SUCCESS
  },
  update: {
    request: types.UPDATE_COMMISSION_AGENT_REQUEST,
    success: types.UPDATE_COMMISSION_AGENT_SUCCESS,
    failure: types.UPDATE_COMMISSION_AGENT_FAILURE,
    reset: types.RESET_UPDATE_COMMISSION_AGENT_SUCCESS
  },
  deactivate: {
    request: types.DELETE_COMMISSION_AGENT_REQUEST,
    success: types.DELETE_COMMISSION_AGENT_SUCCESS,
    failure: types.DELETE_COMMISSION_AGENT_FAILURE,
    reset: types.RESET_DELETE_COMMISSION_AGENT_SUCCESS
  },
  permanentDelete: {
    request: types.DELETE_COMMISSION_AGENT_PERMANENT_REQUEST,
    success: types.DELETE_COMMISSION_AGENT_PERMANENT_SUCCESS,
    failure: types.DELETE_COMMISSION_AGENT_PERMANENT_FAILURE,
    reset: types.RESET_DELETE_COMMISSION_AGENT_PERMANENT
  },
  bankCreate: {
    request: types.CREATE_AGENT_BANK_REQUEST,
    success: types.CREATE_AGENT_BANK_SUCCESS,
    failure: types.CREATE_AGENT_BANK_FAILURE,
    reset: types.RESET_CREATE_AGENT_BANK_SUCCESS
  },
  bankUpdate: {
    request: types.UPDATE_AGENT_BANK_REQUEST,
    success: types.UPDATE_AGENT_BANK_SUCCESS,
    failure: types.UPDATE_AGENT_BANK_FAILURE,
    reset: types.RESET_UPDATE_AGENT_BANK_SUCCESS
  },
  bankDeactivate: {
    request: types.DELETE_AGENT_BANK_REQUEST,
    success: types.DELETE_AGENT_BANK_SUCCESS,
    failure: types.DELETE_AGENT_BANK_FAILURE
  },
  bankPermanentDelete: {
    request: types.DELETE_AGENT_BANK_PERMANENT_REQUEST,
    success: types.DELETE_AGENT_BANK_PERMANENT_SUCCESS,
    failure: types.DELETE_AGENT_BANK_PERMANENT_FAILURE
  }
};

var writeReducers = {};
Object.keys(WRITES).forEach(function(name) {
  writeReducers[name] = createWriteReducer(WRITES[name]);
});

// Error-clearing actions, separate from the success resets above.
var ERROR_RESETS = {
  create: types.RESET_CREATE_COMMISSION_AGENT_ERROR,
  update: types.RESET_UPDATE_COMMISSION_AGENT_ERROR,
  deactivate: types.RESET_DELETE_COMMISSION_AGENT_ERROR,
  bankCreate: types.RESET_CREATE_AGENT_BANK_ERROR,
  bankUpdate: types.RESET_UPDATE_AGENT_BANK_ERROR
};

function withoutKey(map, key) {
  var copy = Object.assign({}, map || {});
  delete copy[key];
  return copy;
}

export function initialState() {
  var state = {
    list: initialListState(),
    banks: initialListState(),

    // agent guid -> that agent's bank records. `banks` above holds only the
    // agent currently being viewed, which is fine for the agent page but not for
    // the batch payout builder, which must check bank details for many agents at
    // once. Keyed here so one agent's fetch cannot evict another's.
    banksByAgent: {},
    // agent guid -> error for a bank lookup that failed for a reason other
    // than "this agent has no bank record" (which lands as [] above).
    banksErrorByAgent: {},

    // The params of the last list request, so Retry and the post-write refresh
    // reuse the filters the user actually had applied instead of resetting them.
    listParams: {},

    // The agent open in the detail page; the record itself lives in
    // list.byGuid.
    detailLoading: false,
    detailError: null,
    agentToEdit: null,

    // Platform users for step 1 of the add-agent wizard. Keycloak returns a
    // bare array with no total, so pagination is prev/next on `hasMore`.
    users: [],
    usersLoading: false,
    usersError: null,
    usersSearch: '',
    usersPage: 1,
    usersSize: 10,

    // user guid -> already a commission agent? Disables the row in step 1.
    existsByGuid: {},

    lookup: { matches: [], loading: false, error: null, notFound: false },

    // Set when the platform user was created but the agent was not. The wizard
    // must show this differently from a plain failure, or the operator retries
    // and a SECOND user is created.
    partialCreate: null
  };
  Object.keys(writeReducers).forEach(function(name) {
    state[name] = initialWriteState();
  });
  return state;
}

// The /exists response shape is undocumented; accept a bare boolean or the
// obvious wrappers rather than guessing one and silently reading undefined.
function readExists(payload) {
  if (typeof payload === 'boolean') return payload;
  if (!payload) return false;
  if (typeof payload.exists === 'boolean') return payload.exists;
  if (typeof payload.data === 'boolean') return payload.data;
  return !!payload.guid;
}

export default function commissionAgentsReducer(state, action) {
  if (state === undefined) state = initialState();
  if (!action || !action.type) return state;

  var next = state;

  // Delegate to the list reducers.
  var list = agentsList(state.list, action);
  var banks = banksList(state.banks, action);
  if (list !== state.list || banks !== state.banks) {
    next = Object.assign({}, next, { list: list, banks: banks });
  }

  // Delegate to the write reducers.
  Object.keys(writeReducers).forEach(function(name) {
    var before = next[name];
    var after = writeReducers[name](before, action);
    if (after !== before) {
      var patch = {};
      patch[name] = after;
      next = Object.assign({}, next, patch);
    }
  });

  switch (action.type) {
    case types.GET_COMMISSION_AGENTS_REQUEST:
      return Object.assign({}, next, {
        listParams: action.payload || {}
      });

    case types.GET_COMMISSION_AGENT_REQUEST:
      return Object.assign({}, next, {
        detailLoading: true,
        detailError: null
      });

    case types.GET_COMMISSION_AGENT_SUCCESS:
      return Object.assign({}, next, {
        detailLoading: false,
        detailError: null
      });

    case types.GET_COMMISSION_AGENT_FAILURE:
      return Object.assign({}, next, {
        detailLoading: false,
        detailError: action.payload || { message: 'Could not load this agent' }
      });

    case types.GET_AGENT_BANKS_BY_AGENT_SUCCESS: {
      // The saga carries the agent guid on `meta`, since the response body does
      // not identify which agent these banks belong to.
      var forAgent = action.meta && action.meta.agentGuid;
      if (!forAgent) return next;
      var entry = {};
      entry[forAgent] = readListResponse(action.payload).rows;
      return Object.assign({}, next, {
        banksByAgent: Object.assign({}, next.banksByAgent, entry),
        banksErrorByAgent: withoutKey(next.banksErrorByAgent, forAgent)
      });
    }

    case types.GET_AGENT_BANKS_BY_AGENT_FAILURE: {
      var failedAgent = action.meta && action.meta.agentGuid;
      if (!failedAgent) return next;
      var error = action.payload || { message: 'Request failed' };
      // 404 is the service's answer for an agent with no bank record at all,
      // which is an answer, not a failure.
      if (error.statusCode === 404) {
        var none = {};
        none[failedAgent] = [];
        return Object.assign({}, next, {
          banksByAgent: Object.assign({}, next.banksByAgent, none),
          banksErrorByAgent: withoutKey(next.banksErrorByAgent, failedAgent)
        });
      }
      var failed = {};
      failed[failedAgent] = error;
      return Object.assign({}, next, {
        banksErrorByAgent: Object.assign({}, next.banksErrorByAgent, failed)
      });
    }

    case types.SET_COMMISSION_AGENT_TO_EDIT:
      return Object.assign({}, next, { agentToEdit: action.payload });

    case types.RESET_COMMISSION_AGENT_TO_EDIT:
      return Object.assign({}, next, { agentToEdit: null });

    case types.GET_USERS_FOR_AGENTS_REQUEST: {
      var requested = action.payload || {};
      return Object.assign({}, next, {
        usersLoading: true,
        usersError: null,
        usersSearch:
          requested.search === undefined ? next.usersSearch : requested.search,
        usersPage: requested.page || 1,
        usersSize: requested.size || next.usersSize
      });
    }

    case types.GET_USERS_FOR_AGENTS_SUCCESS:
      return Object.assign({}, next, {
        usersLoading: false,
        usersError: null,
        users: readListResponse(action.payload).rows
      });

    case types.GET_USERS_FOR_AGENTS_FAILURE:
      return Object.assign({}, next, {
        usersLoading: false,
        usersError: action.payload || { message: 'Could not load users' },
        users: []
      });

    case types.COMMISSION_AGENT_EXISTS_SUCCESS: {
      // The saga echoes back the guid it asked about, since the response body
      // does not identify it.
      var result = action.payload || {};
      if (!result.guid) return next;
      var addition = {};
      addition[result.guid] = readExists(result.response);
      return Object.assign({}, next, {
        existsByGuid: Object.assign({}, next.existsByGuid, addition)
      });
    }

    // Creating an agent invalidates the cached "is this user already an agent"
    // answer for that user. Without this the picker keeps serving the `false` it
    // learned before the create — the row for the agent just created still reads
    // "No agent record" and stays selectable, inviting a second attempt for the
    // same user. The picker only requests guids it has no answer for, so a stale
    // entry is never refetched on its own.
    //
    // The guid is the answer: an agent's guid IS its user's id, which is exactly
    // what the picker looks up.
    case types.CREATE_COMMISSION_AGENT_SUCCESS:
    case types.CREATE_AGENT_WITH_USER_SUCCESS: {
      var created = action.payload || {};
      if (!created.guid) return next;
      var nowTaken = {};
      nowTaken[created.guid] = true;
      return Object.assign({}, next, {
        existsByGuid: Object.assign({}, next.existsByGuid, nowTaken)
      });
    }

    // The mirror image: a permanent delete frees the user to back an agent
    // again, so the cached `true` has to go. Deactivating does NOT belong here —
    // the record still exists, so the guid is still taken.
    case types.DELETE_COMMISSION_AGENT_PERMANENT_SUCCESS: {
      var removed = action.meta && action.meta.guid;
      if (!removed) return next;
      var freed = {};
      freed[removed] = false;
      return Object.assign({}, next, {
        existsByGuid: Object.assign({}, next.existsByGuid, freed)
      });
    }

    case types.LOOKUP_COMMISSION_AGENT_REQUEST:
      return Object.assign({}, next, {
        lookup: { matches: [], loading: true, error: null, notFound: false }
      });

    case types.LOOKUP_COMMISSION_AGENT_SUCCESS: {
      var found = readListResponse(action.payload).rows;
      return Object.assign({}, next, {
        lookup: {
          matches: found,
          loading: false,
          error: null,
          notFound: !found.length
        }
      });
    }

    case types.LOOKUP_COMMISSION_AGENT_FAILURE: {
      // A 404 means "no agent with that identifier" — an ordinary outcome of a
      // lookup, not an error worth shouting about.
      var error = action.payload || {};
      var missing = error.statusCode === 404;
      return Object.assign({}, next, {
        lookup: {
          matches: [],
          loading: false,
          error: missing ? null : error,
          notFound: missing
        }
      });
    }

    case types.CREATE_AGENT_WITH_USER_PARTIAL:
      return Object.assign({}, next, {
        partialCreate: action.payload || null,
        // Not submitting any more, and NOT a success — the wizard stays open on
        // the details step so the operator can see what happened.
        createWithUser: Object.assign({}, next.createWithUser, {
          submitting: false,
          success: false,
          error: (action.payload && action.payload.error) || null
        })
      });

    case types.CREATE_AGENT_WITH_USER_REQUEST:
      // Clear any previous partial state so a fresh attempt starts clean.
      return Object.assign({}, next, { partialCreate: null });

    case types.RESET_LOOKUP_COMMISSION_AGENT:
      return Object.assign({}, next, {
        lookup: { matches: [], loading: false, error: null, notFound: false }
      });

    default:
      break;
  }

  // Error-only resets, table-driven.
  var names = Object.keys(ERROR_RESETS);
  for (var i = 0; i < names.length; i += 1) {
    if (action.type === ERROR_RESETS[names[i]]) {
      var cleared = {};
      cleared[names[i]] = Object.assign({}, next[names[i]], { error: null });
      return Object.assign({}, next, cleared);
    }
  }

  return next;
}
